import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { TailwindParser } from '../../../utils/TailwindParser.js';
import { parseThemeColor } from '../../../utils/theme-helper.js';
import { BREAKPOINTS } from '../../../constants.js';

const VariantPreview = ({ variant, activeKey, disabledPreviewKeys, componentType, previewNode, forcedState, activeBreakpoint = 'default', compact = false }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [themeVersion, setThemeVersion] = useState(0);

    // Listen for theme changes
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (window.SiteTheme && window.SiteTheme.onThemeChange) {
            const cleanup = window.SiteTheme.onThemeChange(() => {
                setThemeVersion(v => v + 1);
            });
            return cleanup;
        }
    }, []);

    // Create a theme-aware color resolver
    const resolveColor = useCallback((colorKey) => {
        if (!colorKey) return undefined;
        if (colorKey.startsWith('#') || colorKey.startsWith('rgb') || colorKey.startsWith('hsl')) {
            return colorKey;
        }
        const themeColor = parseThemeColor(colorKey);
        if (themeColor) return `rgb(${themeColor.replace(/ /g, ', ')})`;
        return undefined;
    }, [themeVersion]);

    const activeStyleItem = (variant?.styles || []).find(s => s.key === activeKey);
    const classString = activeStyleItem && !disabledPreviewKeys.includes(activeKey) ? activeStyleItem.value : '';

    const stateStyles = useMemo(() => {
        return TailwindParser.parseStates(classString, resolveColor);
    }, [classString, resolveColor]);

    const combinedStyle = useMemo(() => {
        // Waterfall Merge: Default -> SM -> MD -> LG... based on activeBreakpoint
        const mergedStyles = {};
        
        // Order of precedence: default, then each breakpoint that is <= active
        const activeWidth = BREAKPOINTS[activeBreakpoint] || 0;
        const sortedBps = Object.keys(BREAKPOINTS).sort((a, b) => BREAKPOINTS[a] - BREAKPOINTS[b]);
        
        sortedBps.forEach(bp => {
            const bpWidth = BREAKPOINTS[bp];
            if (bpWidth <= activeWidth) {
                 // This breakpoint is active
                 const stylesForBp = stateStyles[bp];
                 if (stylesForBp) {
                     // Always merge normal state
                     if (stylesForBp.normal) Object.assign(mergedStyles, stylesForBp.normal);
                     
                     // Interaction states
                     if (isHovered && stylesForBp.hover) Object.assign(mergedStyles, stylesForBp.hover);
                     if (isFocused && stylesForBp.focus) Object.assign(mergedStyles, stylesForBp.focus);
                     if (isActive && stylesForBp.active) Object.assign(mergedStyles, stylesForBp.active);
                     
                     // Forced state override
                     if (forcedState && forcedState !== 'normal' && stylesForBp[forcedState]) {
                         Object.assign(mergedStyles, stylesForBp[forcedState]);
                     }
                 }
            }
        });
        
        return mergedStyles;
    }, [stateStyles, activeBreakpoint, isHovered, isFocused, isActive, forcedState]);
    
    let content = null;
    let extraClasses = "";
    let imageSrc = null;

    if (previewNode) {
        const textComp = previewNode.attachedComponents?.find(c => c.type === 'Text');
        if (textComp?.props?.content) content = textComp.props.content;
        else if (previewNode.props.content) content = previewNode.props.content;
        else if (previewNode.type === 'Text' && previewNode.props.content) content = previewNode.props.content;

        const imageComp = previewNode.attachedComponents?.find(c => c.type === 'Image');
        if (imageComp?.props?.src) imageSrc = imageComp.props.src;

        if (previewNode.props.className) {
            extraClasses = previewNode.props.className.split(' ')
                .filter(c => c.startsWith('rounded'))
                .join(' ');
        }
    }

    const handlers = {
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        onFocus: () => setIsFocused(true),
        onBlur: () => setIsFocused(false),
        onMouseDown: () => setIsActive(true),
        onMouseUp: () => setIsActive(false),
        tabIndex: 0
    };

    const containerClasses = compact 
        ? "p-2 flex items-center justify-center min-h-[60px] w-full h-full"
        : "bg-white p-8 rounded shadow border border-slate-100 min-h-[150px] flex items-center justify-center";

    if (componentType === 'Typography' || componentType === 'Text') {
        return (
            <div className={containerClasses}>
                <span className={`${extraClasses} cursor-default`} style={combinedStyle} {...handlers}>
                    {content || "The quick brown fox jumps over the lazy dog."}
                </span>
            </div>
        );
    }
    if (componentType === 'Button') {
        return (
            <div className={containerClasses}>
                <button className={`${extraClasses}`} style={combinedStyle} {...handlers}>
                    {content || "Sample Button"}
                </button>
            </div>
        );
    }
    if (imageSrc) {
            return (
            <div className={containerClasses}>
                <img src={imageSrc} className={`${extraClasses}`} style={{ ...combinedStyle, maxWidth: '100%', maxHeight: compact ? '60px' : '200px' }} alt="Preview" {...handlers} />
            </div>
        );
    }
    return (
        <div className={containerClasses}>
            <div className={`${compact ? '' : 'w-32 h-32 border border-dashed border-gray-300'} ${extraClasses} flex items-center justify-center`} style={combinedStyle} {...handlers}>
                {content || (compact ? "Preview" : "Content")}
            </div>
        </div>
    );
};

export default VariantPreview;
